import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { User, Calendar, Award, Eye } from 'lucide-react';
import { Project } from '../../../types/project.types';
import { ProjectJudgingData, ProjectScore } from '../../../types/judging.types';
import { judgingService } from '../../../services/judging.service';
import RubricScoreDetailsDialog from './RubricScoreDetailsDialog';

interface ProjectScoresDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project;
}

const ProjectScoresDialog: React.FC<ProjectScoresDialogProps> = ({
  open,
  onClose,
  project,
}) => {
  const [judgingData, setJudgingData] = useState<ProjectJudgingData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScore, setSelectedScore] = useState<
    | (ProjectScore & {
        rubricId?: string;
        rubricScores?: { questionId: string; pointsAwarded: number }[];
      })
    | null
  >(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  useEffect(() => {
    if (!open || !project.id) {
      setJudgingData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = judgingService.subscribeToProjectScores(
      project.id,
      (data) => {
        setJudgingData(data);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [open, project.id]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Award size={20} />
          <Typography variant="h6">
            Scoring Occurrences: {project.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !judgingData || judgingData.scores.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="body1" color="text.secondary">
              No scores have been submitted for this project yet.
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Summary Stats */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.lighter' }}>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Judges
                  </Typography>
                  <Typography variant="h6">
                    {judgingData.totalScores}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Average Score
                  </Typography>
                  <Typography variant="h6">
                    {judgingData.averageScore.toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Scores Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Judge ID</TableCell>
                    <TableCell>Judge Name</TableCell>
                    <TableCell align="center">Total Score</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Submitted</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {judgingData.scores.map((score) => (
                    <TableRow key={score.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <User size={16} />
                          <Typography variant="body2" fontFamily="monospace">
                            {score.judgeId.slice(0, 12)}...
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{score.judgeName}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={score.score}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={score.status}
                          size="small"
                          color={
                            score.status === 'completed'
                              ? 'success'
                              : score.status === 'in_progress'
                                ? 'warning'
                                : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Calendar size={14} />
                          <Typography variant="caption">
                            {formatDate(score.createdAt)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {(score as any).rubricId &&
                        (score as any).rubricScores ? (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedScore(score as any);
                              setIsDetailsDialogOpen(true);
                            }}
                            title="View Rubric Details"
                          >
                            <Eye size={18} />
                          </IconButton>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Rubric Score Details Dialog */}
      {selectedScore && (
        <RubricScoreDetailsDialog
          open={isDetailsDialogOpen}
          onClose={() => {
            setIsDetailsDialogOpen(false);
            setSelectedScore(null);
          }}
          score={selectedScore}
        />
      )}
    </Dialog>
  );
};

export default ProjectScoresDialog;
